/**
 * Display configuration for stellar object renderers.
 * Contains only visual data (colors, sizes, names) — no generation logic.
 * Injected into SunRenderer and PlanetRenderer.
 */

export interface SpectralClassInfo {
  readonly name: string;
  readonly color: readonly [r: number, g: number, b: number];
  readonly coronaScale: number;
  readonly noiseScale: number;
  readonly speed: number;
}

export interface PlanetTypeInfo {
  readonly name: string;
  readonly colorDeep: readonly [number, number, number];
  readonly colorShore: readonly [number, number, number];
  readonly colorCoast: readonly [number, number, number];
  readonly colorLand: readonly [number, number, number];
  readonly colorPeak: readonly [number, number, number];
  readonly colorAtmos: readonly [number, number, number];
  readonly colorCloud: readonly [number, number, number];
  readonly waterLevel: number;
  readonly peakLevel: number;
  readonly hasAtmosphere: boolean;
  readonly hasClouds: boolean;
  readonly cloudDensity: number;
  readonly cloudSpeed: number;
  readonly noiseScale: number;
}

export interface StellarRenderConfig {
  readonly spectralClasses: Record<string, SpectralClassInfo>;
  readonly planetTypes: Record<string, PlanetTypeInfo>;
  readonly sunBaseSize: number;
  readonly planetBaseSize: number;
}

export function defaultStellarRenderConfig(): StellarRenderConfig {
  return {
    spectralClasses: {
      M: { name: 'Red Dwarf',   color: [1.0, 0.2, 0.0],  coronaScale: 1.2,  noiseScale: 2.2, speed: 0.08 },
      K: { name: 'Orange Star', color: [1.0, 0.67, 0.13], coronaScale: 1.25, noiseScale: 2.1, speed: 0.08 },
      G: { name: 'Yellow Star', color: [1.0, 0.87, 0.27], coronaScale: 1.3,  noiseScale: 2.0, speed: 0.08 },
      F: { name: 'White Star',  color: [1.0, 1.0, 1.0],   coronaScale: 1.35, noiseScale: 1.9, speed: 0.08 },
      O: { name: 'Blue Giant',  color: [0.4, 0.8, 1.0],   coronaScale: 1.4,  noiseScale: 1.8, speed: 0.08 },
    },
    planetTypes: {
      terran: {
        name: 'Terran',
        colorDeep:  [0.0, 0.0, 0.33],
        colorShore: [0.0, 0.33, 0.66],
        colorCoast: [0.82, 0.71, 0.55],
        colorLand:  [0.13, 0.53, 0.13],
        colorPeak:  [0.4, 0.4, 0.4],
        colorAtmos: [0.53, 0.8, 1.0],
        colorCloud: [1.0, 1.0, 1.0],
        waterLevel: 0.5,
        peakLevel:  0.75,
        hasAtmosphere: true,
        hasClouds: true,
        cloudDensity: 0.5,
        cloudSpeed: 0.05,
        noiseScale: 2.0,
      },
      desert: {
        name: 'Desert',
        colorDeep:  [0.53, 0.27, 0.0],
        colorShore: [0.8, 0.4, 0.0],
        colorCoast: [0.8, 0.53, 0.13],
        colorLand:  [1.0, 0.67, 0.27],
        colorPeak:  [0.4, 0.2, 0.0],
        colorAtmos: [1.0, 0.8, 0.53],
        colorCloud: [0.9, 0.85, 0.7],
        waterLevel: 0.0,
        peakLevel:  0.6,
        hasAtmosphere: true,
        hasClouds: false,
        cloudDensity: 0.3,
        cloudSpeed: 0.05,
        noiseScale: 1.5,
      },
      ice: {
        name: 'Ice',
        colorDeep:  [0.27, 0.4, 0.53],
        colorShore: [0.67, 0.87, 1.0],
        colorCoast: [0.67, 0.85, 1.0],
        colorLand:  [1.0, 1.0, 1.0],
        colorPeak:  [0.67, 0.8, 1.0],
        colorAtmos: [0.8, 1.0, 1.0],
        colorCloud: [1.0, 1.0, 1.0],
        waterLevel: 0.4,
        peakLevel:  0.6,
        hasAtmosphere: true,
        hasClouds: true,
        cloudDensity: 0.3,
        cloudSpeed: 0.05,
        noiseScale: 2.5,
      },
      lava: {
        name: 'Lava',
        colorDeep:  [1.0, 0.0, 0.0],
        colorShore: [1.0, 0.27, 0.0],
        colorCoast: [0.4, 0.1, 0.0],
        colorLand:  [0.13, 0.0, 0.0],
        colorPeak:  [0.07, 0.0, 0.0],
        colorAtmos: [1.0, 0.33, 0.0],
        colorCloud: [0.13, 0.13, 0.13],
        waterLevel: 0.3,
        peakLevel:  0.6,
        hasAtmosphere: true,
        hasClouds: true,
        cloudDensity: 0.6,
        cloudSpeed: 0.1,
        noiseScale: 3.0,
      },
    },
    sunBaseSize: 1.8,
    planetBaseSize: 1.0,
  };
}
